import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FishSimple, X } from "@phosphor-icons/react";

const DISMISS_KEY = "joke-ribbon-dismissed";
const SHOW_AFTER_PX = 400;

/**
 * Small floating ribbon that nudges mid-scroll Shop visitors to drop a joke.
 * - Appears after the user scrolls past 400px (so it never blocks the hero)
 * - Dismissable with an X; dismissal is remembered for the browser session
 * - Fixed bottom-right on desktop, bottom-center on mobile
 */
export default function JokeRibbon() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "1"; }
    catch { return false; }
  });

  useEffect(() => {
    if (dismissed) return;
    const onScroll = () => {
      if (window.scrollY > SHOW_AFTER_PX) setVisible(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // in case page already scrolled on mount
    return () => window.removeEventListener("scroll", onScroll);
  }, [dismissed]);

  function dismiss(e) {
    e.preventDefault();
    e.stopPropagation();
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  }

  if (dismissed || !visible) return null;

  return (
    <div
      data-testid="joke-ribbon"
      className="fixed z-30 bottom-5 right-5 md:bottom-6 md:right-6 left-5 md:left-auto animate-in fade-in slide-in-from-bottom-4 duration-500"
    >
      <Link
        to="/submit"
        data-testid="joke-ribbon-link"
        className="group flex items-center gap-3 bg-[#C84A31] text-white rounded-sm pl-4 pr-2 py-3 shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all border-2 border-[#C84A31] hover:bg-[#A63D28] max-w-full"
      >
        <FishSimple
          size={22}
          weight="duotone"
          className="flex-shrink-0 text-white group-hover:rotate-[-12deg] transition-transform"
        />
        <div className="flex-1 min-w-0 leading-tight">
          <div className="font-display text-sm md:text-base truncate">
            Got a fish joke?
          </div>
          <div className="text-[11px] uppercase tracking-widest text-white/80 truncate">
            Drop it in · 10 seconds
          </div>
        </div>
        <button
          data-testid="joke-ribbon-dismiss"
          onClick={dismiss}
          aria-label="Dismiss"
          className="flex-shrink-0 p-1.5 rounded-sm hover:bg-white/15 transition-colors"
        >
          <X size={14} />
        </button>
      </Link>
    </div>
  );
}
